import React, { useReducer, useEffect, useContext } from "react";
import {
  WmButton,
  WmProgressMonitor,
  WmProgressSlice,
  WmProgressIndicator,
} from "@watermarkinsights/ripple-react";
import OpenDrillDownView from "../common/OpenDrillDownView";
import { openFlyout } from "../../static/js/commons";
import { sendRequest } from "../../common/http";
import { AppContext, AppContextInterface, OutcomesTerminology } from "../../contexts/AppContext";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import { useFormatMessage } from "../common/helper";
import ChartIconSvg from "../common/chartIconSvg";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import { ReportingYear } from "../common/interfaces/Interface";

const drilldownFilters = [
  { value: "mapped", name: translate("app.drilldown.mapped") },
  { value: "not_mapped", name: translate("app.drilldown.notMapped") },
];

export interface ProgramILOMappingProps {
  onComponentLoad: (type: string) => void;
  reportingYears: ReportingYear[] | null;
}

interface ProgramMapping {
  isGeneralEducation: boolean;
  mappedPrograms: number;
  parentOutcome: string;
  reportingYearStartDate: string;
  sortOrder: number;
  title: string;
  updatedAt: string;
  uuid: string;
}
interface Children {
  children: Children[];
  level: number;
  name: string;
  show_stats: boolean;
  status: string;
  total_mappings: number;
  total_outcomes: number;
  uuid: string;
  type: string;
}

interface Hierarchy {
  level: number;
  name: string;
  showStats: boolean;
  uuid: string;
  children: Children[];
}

const popover = (count: number, total: number) => `${count}/${total}`;

const initialState = {
  drilldownName: "",
  drilldownType: "programILO",
  drilldownStatus: "any",
  isDrilldownOpen: false,
  programMappings: [],
  totalPrograms: 0,
  hierarchy: [],
  showTable: false,
  showLoader: true,
  apiError: false,
};

interface ProgramILOMappingState {
  drilldownName: string;
  drilldownType: string;
  drilldownStatus: string;
  isDrilldownOpen: boolean;
  programMappings: ProgramMapping[];
  totalPrograms: number;
  hierarchy: Hierarchy[];
  showTable: boolean;
  showLoader: boolean;
  apiError: boolean;
}

interface ProgramMappingResponse {
  totalPrograms: number;
  programMappings: ProgramMapping[];
}

interface ProgramILOMappingAction {
  type: string;
  drilldownName: string;
  drilldownStatus: string;
  programMapping: ProgramMappingResponse;
  apiError: boolean;
  showLoader: boolean;
  hierarchy: Hierarchy[];
}
const defaultAction = {
  type: "",
  hierarchy: [],
  drilldownName: "",
  drilldownStatus: "",
  programMapping: { totalPrograms: 0, programMappings: [] },
  apiError: false,
  showLoader: false,
};

const reducer = (
  state: ProgramILOMappingState,
  action: ProgramILOMappingAction
) => {
  switch (action.type) {
    case "setProgramMapping":
      return {
        ...state,
        programMappings: action.programMapping.programMappings,
        totalPrograms: action.programMapping.totalPrograms,
      };
    case "openDrilldown":
      return {
        ...state,
        isDrilldownOpen: true,
      };
    case "closeDrilldown":
      return {
        ...state,
        isDrilldownOpen: false,
        showTable: false,
        hierarchy: [],
      };
    case "SetDrilldownData":
      return {
        ...state,
        drilldownName: action.drilldownName,
        drilldownStatus: action.drilldownStatus,
      };
    case "setHierarchy":
      return { ...state, hierarchy: action.hierarchy, showTable: true };
    case "setShowLoader":
      return { ...state, showLoader: action.showLoader };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};

export default function ProgramILOMappings({
  onComponentLoad,
  reportingYears,
}: ProgramILOMappingProps) {
  const { leads, institution, outcomesTerminology } =
    useContext<AppContextInterface>(AppContext);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (reportingYears) {
      let uri = "/program_ilo_mappings";
      let currentReportingYear;
      if (reportingYears?.length) {
        currentReportingYear = reportingYears?.find(
          (reportingYear) => reportingYear.isCurrent
        );
        uri += currentReportingYear?.uuid
          ? `?reporting_year_uuid=${currentReportingYear.uuid}`
          : "";
      }
      const applyResult = (result: ProgramMappingResponse) => {
        dispatch({
          ...defaultAction,
          type: "setProgramMapping",
          programMapping: result,
        });
        dispatch({
          ...defaultAction,
          type: "setShowLoader",
          showLoader: false,
        });
        onComponentLoad("programILOMappings");
      };

      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setApiError",
          apiError: true,
        });
        dispatch({
          ...defaultAction,
          type: "setShowLoader",
          showLoader: false,
        });
        onComponentLoad("programILOMappings");
      };

      sendRequest({ uri: uri, camelize: true }, applyResult, onError);
    }
  }, [reportingYears]);

  const getDrilldownHierarchy = (uuid: string) => {
    openDrilldownView();

    const applyResult = (result: Hierarchy[]) => {
      dispatch({
        ...defaultAction,
        type: "setHierarchy",
        hierarchy: result,
      });
    };
    sendRequest(
      { uri: `/hierarchy/programs_mapped_ilo/${uuid}`, camelize: true },
      applyResult
    );
  };

  const openDrilldownView = () => {
    dispatch({
      ...defaultAction,
      type: "openDrilldown",
    });
  };
  const closeDrilldown = () => {
    dispatch({
      ...defaultAction,
      type: "closeDrilldown",
    });
  };

  const openDrillDown = (name: string, uuid: string, status: string) => {
    getDrilldownHierarchy(uuid);
    dispatch({
      ...defaultAction,
      type: "SetDrilldownData",
      drilldownName: name,
      drilldownStatus: status,
    });
  };

  const drilldownSubHeader = useIntl().formatMessage({
    id: "app.programILOMapping.programsMappedILO",
  });
  const exportMissionOutcomes = useIntl().formatMessage({
    id: "app.programILOMapping.exportMissionOutcomes",
  });
  const totalPrograms = useIntl().formatMessage(
    { id: "app.programProgressCard.totalPrograms" },
    { totalPrograms: state.totalPrograms }
  );
  const showData = () => !state.showLoader && !state.apiError;
  return (
    <>
      <div
        className={
          state.apiError ? "paper-card" : "paper-card program-ilo-widget"
        }
      >
        <div className="header">
          <div className="info">
            <h3 className="title">
              {translate("app.programILOMapping.programsMappedILO")}
            </h3>
            <p className="description">
              {translate("app.programILOMapping.reviewPrograms")}
            </p>
          </div>
          <WmButton
            hidden={true}
            button-type="secondary"
            label-for-identical-buttons={exportMissionOutcomes}
          >
            {translate("app.programILOMapping.export")}
          </WmButton>
        </div>

        <div className="body ilo-map-body">
          {state.showLoader && (
            <div className="loader-position">
              <WidgetLoader
                loaderContainerClasses="loader-container"
                loaderClasses="widget-loader"
              />
            </div>
          )}
          {state.apiError && <ApiError containerClasses="api-error" />}
          {showData() && (
            <Body
              programMappings={state.programMappings}
              totalPrograms={state.totalPrograms}
              openDrillDown={openDrillDown}
              outcomesTerminology={outcomesTerminology}
            />
          )}
        </div>
      </div>

      <OpenDrillDownView
        setIsDrilldownOpen={closeDrilldown}
        drilldownName={state.drilldownName}
        drilldownType={state.drilldownType}
        drilldownStatus={state.drilldownStatus}
        total={totalPrograms}
        showTable={state.showTable}
        completeHierarchy={state.hierarchy}
        filters={drilldownFilters}
        subHeader={drilldownSubHeader}
        leads={leads}
        isVisible={state.isDrilldownOpen}
        open={state.isDrilldownOpen}
        id="program-ilo-drill-down-view"
      ></OpenDrillDownView>
    </>
  );
}

interface ProgramIloMappingChartProp {
  programMappings: ProgramMapping[];
  totalPrograms: number;
  openDrillDown: (name: string, uuid: string, status: string) => void;
  outcomesTerminology?: OutcomesTerminology | null;
}

const Body = (props: ProgramIloMappingChartProp) => {
  const formatMessage = useFormatMessage();
  
  const noOutcomesMessage = formatMessage(
    { id: "app.programILOMapping.noOutcomes" },
    { 
      outcome_plural: props.outcomesTerminology?.outcomes?.plural || "..." 
    }
  );

  if (props.programMappings.length === 0) {
    return (
      <div id="ilo-map-empty-state">
        <div className="ilo-empty-state">
          <ChartIconSvg />
        </div>
        <h3 className="no-outcomes-text">
          {noOutcomesMessage}
        </h3>
      </div>
    );
  } else {
    return (
      <div id="ilo-chart-container">
        <h4 className="title">
          {translate("app.programILOMapping.programMappingStatusILO")}
        </h4>
        <div className="legend-wrapper">
          <div className="legend" aria-hidden="true">
            <div className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: "rgb(107, 107, 107)" }}
              ></div>
              <div className="legend-text">
                {translate("app.drilldown.notMapped")}
              </div>
            </div>
            <div className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: "rgb(8, 128, 0)" }}
              ></div>
              <div className="legend-text">
                {translate("app.drilldown.mapped")}
              </div>
            </div>
          </div>
        </div>
        <div className="charts ilo-charts" id="program-ilo-mapping">
          <ProgramIloMappingChart
            programMappings={props.programMappings}
            totalPrograms={props.totalPrograms}
            openDrillDown={props.openDrillDown}
          />
        </div>
      </div>
    );
  }
};

const ProgramIloMappingChart = (props: ProgramIloMappingChartProp) => {
  const programsNotMapped = useIntl().formatMessage({
    id: "app.programILOMapping.programsNotMapped",
  });
  const programsMapped = useIntl().formatMessage({
    id: "app.programILOMapping.programsMapped",
  });
  const mappedText = useIntl().formatMessage({
    id: "app.drilldown.mapped",
  });
  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped",
  });
  const drilldownByProgram = useIntl().formatMessage({
    id: "app.programILOMapping.drilldownByProgram",
  });

  return (
    <WmProgressMonitor id="ilo-progress" breakpoint="10px">
      {props.programMappings.map((programMapping: ProgramMapping) => {
        const {
          title: iloTitle,
          mappedPrograms: mapped,
          isGeneralEducation: isGenEd,
          uuid,
        } = programMapping;

        return (
          <React.Fragment key={`fragment-${uuid}`}>
            <WmProgressIndicator
              label={iloTitle}
              onWmProgressIndicatorButtonTriggered={(e) => {
                props.openDrillDown(
                  iloTitle,
                  uuid,
                  e.detail.legend === mapped ? "mapped" : "not_mapped"
                );
              }}
            >
              <WmProgressSlice
                key={`${uuid}-mapped`}
                amount={mapped.toString()}
                popover-title={popover(mapped, props.totalPrograms)}
                popover-text={programsMapped}
                legend={mappedText}
                popover-button-text={drilldownByProgram}
              />
              <WmProgressSlice
                key={`${uuid}-not-mapped`}
                amount={(props.totalPrograms - mapped).toString()}
                popover-title={popover(
                  props.totalPrograms - mapped,
                  props.totalPrograms
                )}
                popover-text={programsNotMapped}
                legend={notMapped}
                popover-button-text={drilldownByProgram}
              />
            </WmProgressIndicator>
            {isGenEd && (
              <div id={`gened-${uuid}`} className="gened-tag">
                {translate("app.programILOMapping.genEd")}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </WmProgressMonitor>
  );
};
